const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL || '/api';

let _activeBoxId: string | null = null;

export function setActiveBoxId(boxId: string | null) {
  _activeBoxId = boxId;
}

export function getActiveBoxId(): string | null {
  return _activeBoxId;
}

function getToken(): string | null {
  return localStorage.getItem('token');
}

function redirectToLogin() {
  localStorage.removeItem('token');
  localStorage.removeItem('tokenExpirationDate');
  window.location.href = '/login';
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (_activeBoxId) {
    headers['box-id'] = _activeBoxId;
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers,
  });

  if (response.status === 401) {
    redirectToLogin();
    throw new Error('Unauthorized');
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({ message: response.statusText }));
    throw { status: response.status, message: body.message || response.statusText };
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

export function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  return request<T>(path, { method: 'GET', signal });
}

export function post<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined });
}

export function put<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'PUT', body: JSON.stringify(body) });
}

export function del<T>(path: string): Promise<T> {
  return request<T>(path, { method: 'DELETE' });
}

export interface SSEEvent {
  taskId: string;
  step: string;
  progress: number;
  message: string;
  done: boolean;
  error?: string;
}

export async function streamSSE(
  path: string,
  onEvent: (event: SSEEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const token = getToken();
  const headers: Record<string, string> = { Accept: 'text/event-stream' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${path}`, { headers, signal });

  if (response.status === 401) {
    redirectToLogin();
    throw new Error('Unauthorized');
  }
  if (!response.ok) {
    throw new Error(`SSE request failed: ${response.status}`);
  }
  if (!response.body) {
    throw new Error('No response body for SSE stream');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      const json = line.slice(6).trim();
      if (!json) continue;
      try {
        onEvent(JSON.parse(json));
      } catch {
        // skip malformed events
      }
    }
  }
}
